/* A colour field that shows a colour, and says so when it has none.
 *
 * TWO BROKEN VERSIONS THIS REPLACES
 * ---------------------------------
 * 1. The builder's project settings passed `type="color"` to the kit's
 *    `Input`. That component styles a TEXT box — full width, text
 *    padding, a border — and a native colour input inside it collapses
 *    into a thin horizontal strip. The audit's words were "el campo
 *    Color se ve como una línea" (finding B11).
 * 2. The console's Marca tab hand-rolled a swatch plus a text box, and
 *    bound the swatch to a placeholder when the field was empty — which
 *    painted a colour the client did not have and stated in a tooltip
 *    that it did.
 *
 * THE RULE THIS ENCODES: an empty field shows NO colour. Not black, not
 * a worked example, not a guessed default. When a caller knows the
 * platform falls back to something it cannot name here, it passes
 * `unsetLabel` and the field says "unset" beside an empty swatch.
 * Showing a plausible colour is how somebody ships a brand they were
 * just told was already in effect.
 *
 * The swatch is a real `<input type="color">` so the OS picker still
 * works; it is sized as a square and paired with a hex box, because the
 * hex is what people paste out of a brand guide.
 */

import { useId, type ReactNode } from "react";

import { cx } from "./layout";

export interface ColorFieldProps {
  label: string;
  /** A `#rrggbb` string, or empty for "not set". */
  value: string;
  onChange: (hex: string) => void;
  /** Shown in the hex box only. NEVER painted in the swatch. */
  placeholder?: string;
  help?: string;
  disabled?: boolean;
  /** When given, an empty value renders this chip beside the swatch. */
  unsetLabel?: string;
  /** Tooltip for the chip — say what applies instead, and why it is not
   *  shown here. */
  unsetHint?: string;
  className?: string;
}

export function ColorField({
  label,
  value,
  onChange,
  placeholder,
  help,
  disabled = false,
  unsetLabel,
  unsetHint,
  className,
}: ColorFieldProps): ReactNode {
  const id = useId();
  const isSet = value.trim() !== "";
  return (
    <div className={cx("min-w-0", className)}>
      <label
        htmlFor={id}
        className="mb-1 block text-xs font-semibold text-neutral-700"
      >
        {label}
      </label>
      <div className="flex items-center gap-2">
        <span
          className={cx(
            "relative inline-flex h-9 w-9 flex-shrink-0 overflow-hidden rounded-[10px] border",
            isSet ? "border-control" : "border-dashed border-control",
          )}
          title={isSet ? value : unsetHint}
        >
          <input
            id={id}
            type="color"
            aria-label={label}
            disabled={disabled}
            // An unset field paints nothing. `#ffffff` is the neutral the
            // browser needs for the picker to open somewhere; the dashed
            // border and the chip are what tell the reader it is unset.
            // color-gate: allow <input type="color"> only accepts #rrggbb
            value={isSet ? value : "#ffffff"}
            onChange={(e) => onChange(e.target.value.toUpperCase())}
            className="absolute -inset-2 h-[calc(100%+1rem)] w-[calc(100%+1rem)] cursor-pointer border-0 bg-transparent p-0 disabled:cursor-not-allowed"
          />
        </span>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          spellCheck={false}
          aria-label={label}
          className="min-w-0 flex-1 rounded-[10px] border border-control px-3 py-2 font-mono text-[13px] text-neutral-800 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:bg-neutral-50"
        />
        {!isSet && unsetLabel !== undefined && (
          <span
            title={unsetHint}
            className="flex-shrink-0 rounded-full bg-neutral-100 px-2 py-0.5 text-2xs font-semibold text-neutral-600"
          >
            {unsetLabel}
          </span>
        )}
      </div>
      {help !== undefined && (
        <p className="mt-1 text-xs text-neutral-500">{help}</p>
      )}
    </div>
  );
}
